import React, { useState, useRef, useEffect } from 'react';

import { useRouter } from 'next/router';
import { FaChevronDown } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  mobile,
  desktop,
  RadioButton,
  RadioButtonGroup,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useGridFacets } from '../context/GridDataContexts';
import useGridInputs from '../hooks/useGridInputs';
import { scrollToTopOfGrid } from '../utils/scroll';
import {
  getSanitizedSortOptions,
  getSortIdRBKey,
  isSortOptionSelected,
  updateSortInUrlParams,
} from '../utils/sortOptions';

const DynamicFormattedMessage = FormattedMessage;

const SortDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SortDropdownButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  height: 48px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  border-radius: ${({ $isOpen }) => ($isOpen ? '4px 4px 0 0' : '4px')};
  border: 0.5px solid ${({ theme }) => theme.colors.sortDropdownLight};
  background: ${({ theme }) => theme.colors.palette.white};
  cursor: pointer;
  width: max-content;
  white-space: nowrap;
  background-color: transparent;
  transition: background-color 0.2s;

  ${desktop`
    &:hover {
      background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgHover};
    }

    &:active {
      background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgActive};
    }
  `}

  ${mobile`
    width: 100%;
    justify-content: space-between;
    border: 1px solid ${({ theme }) => theme.colors.sortDropdownLight};
    height: 56px;
  `}
`;

const Chevron = styled(FaChevronDown)<{ $isOpen: boolean }>`
  color: ${({ theme }) => theme.colors.sortDropdownChevron};
  flex-shrink: 0;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  margin-left: 12px;
`;

const BaseLabel = styled.span`
  text-align: center;
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
`;

const SortByLabel = styled(BaseLabel)`
  color: ${({ theme }) => theme.colors.sortDropdownSortBy};

  ${mobile`
    font-weight: 600;
    color: ${({ theme }) => theme.colors.sortDropdownDark};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    line-height: 20px;
  `}
`;

const SelectedOptionMobileWrapper = styled.div`
  display: flex;
  height: 20px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 2px;
  border: 1px solid ${({ theme }) => theme.colors.sortDropdownDark};
  margin-left: 8px;
`;

const SelectedOptionMobileLabel = styled(BaseLabel)`
  color: ${({ theme }) => theme.colors.sortDropdownDark};
  text-align: center;
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

const SelectedOptionLabel = styled(BaseLabel)`
  color: ${({ theme }) => theme.colors.sortDropdownDark};
  margin-left: 4px;
`;

const DropdownList = styled(RadioButtonGroup)`
  ${desktop`
    position: absolute;
    top: 100%;
    left: 0;
  `}
  display: flex;
  flex-direction: column;
  padding: 16px 16px 16px 12px;
  align-items: flex-start;
  gap: 24px;
  border-radius: 0 0 4px 4px;
  border: 0.5px solid ${({ theme }) => theme.colors.sortDropdownLight};
  border-top: none;
  background: ${({ theme }) => theme.colors.palette.white};
  z-index: 100;
  width: max-content;
  min-width: 100%;
  overflow: hidden;
  opacity: 1;
  max-height: 800px;
  ${({ $isOpen }) =>
    !$isOpen &&
    `
    padding: 0 16px 0 12px;
    opacity: 0;
    border: 0;
    max-height: 0px;
    `};
  transition:
    padding 0.3s ease,
    opacity 0.3s ease,
    border 0.3s ease,
    max-height 0.3s ease;
`;

const DropdownListLabel = styled.span<{ $isSelected: boolean }>`
  color: ${({ theme }) => theme.colors.sortDropdownDark};
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ $isSelected }) => ($isSelected ? '700' : '400')};
  line-height: 24px;

  text-decoration-line: ${({ $isSelected }) =>
    $isSelected ? 'underline' : 'none'};
`;

const RadioButtonOuter = styled.div`
  max-height: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  background: transparent;
  gap: 4px;
  cursor: pointer;
  width: 100%;
`;

const RadioInputStyle = css`
  border: 1px solid ${({ theme }) => theme.colors.sortDropdownDark};
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin: 4px;
`;

const RadioInputCheckedStyle = css`
  border: 1px solid ${({ theme }) => theme.colors.sortDropdownDark};
  width: 16px;
  height: 16px;
  background: ${({ theme }) => theme.colors.palette.white};
  svg {
    width: 10px;
    font-size: 10px;
    color: ${({ theme }) => theme.colors.sortDropdownDark};
  }
`;

const RadioInputFocusStyle = css`
  box-shadow: none;
`;

interface SortDropdownProps {
  /** Staged sort key — when provided, display this instead of the URL-selected sort */
  stagedSort?: string | null;
  /** Called with the selected sort key instead of updating the URL directly */
  onSortSelect?: (sort: string) => void;
}

const SortDropdown = ({ stagedSort, onSortSelect }: SortDropdownProps = {}) => {
  const router = useRouter();
  const { isVip } = useMembership();
  const { sortOptions: builderSortOptions } = useGridInputs();
  const { sortOptions } = useGridFacets();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();

  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => {
    setIsOpen((isOpen) => !isOpen);
  };
  // Close the dropdown if the user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  if (!sortOptions) {
    return null;
  }

  const selectedSortOption = stagedSort
    ? sortOptions.find((option) => option.sort_by === stagedSort)
    : sortOptions.find((option) =>
        isSortOptionSelected({ status: option.status })
      );

  const sanitizedSortOptions = getSanitizedSortOptions({
    builderSortOptions,
    sortOptions,
    isVip,
    excludeSelectedOption: false,
  });

  const handleSortOptionClick = (key) => {
    if (onSortSelect) {
      onSortSelect(key);
      return;
    }
    updateSortInUrlParams({
      router,
      sort: key,
    });
    if (!isMobile) {
      setIsOpen(false);
    }
    scrollToTopOfGrid({ isMobile });
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % sanitizedSortOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(
        (prev) =>
          (prev - 1 + sanitizedSortOptions.length) % sanitizedSortOptions.length
      );
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSortOptionClick(sanitizedSortOptions[focusedIndex].sort_by);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  if (sanitizedSortOptions?.length <= 1) {
    return null;
  }

  const selectedOptionText = (
    <DynamicFormattedMessage
      id={`grid_dynamic.${getSortIdRBKey(selectedSortOption?.sort_by)}`}
      defaultMessage={selectedSortOption?.display_name || 'Select'}
    />
  );

  return (
    <SortDropdownContainer ref={containerRef}>
      <SortDropdownButton
        $isOpen={isOpen}
        type="button"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby="sort-label"
        onKeyDown={handleKeyDown}
        ref={buttonRef}
        data-autotag="sort-dropdown"
      >
        <SortByLabel>
          <FormattedMessage
            id="grid_static.sort_by"
            defaultMessage="Sort By:"
          />
          {isMobile && (
            <SelectedOptionMobileWrapper>
              <SelectedOptionMobileLabel>
                {selectedOptionText}
              </SelectedOptionMobileLabel>
            </SelectedOptionMobileWrapper>
          )}
        </SortByLabel>
        {!isMobile && (
          <SelectedOptionLabel>{selectedOptionText}</SelectedOptionLabel>
        )}
        <Chevron $isOpen={isOpen} />
      </SortDropdownButton>
      <DropdownList
        role="listbox"
        aria-labelledby="sort-label"
        aria-orientation="vertical"
        $isOpen={isOpen}
      >
        {sanitizedSortOptions.map(
          ({ display_name: displayName, sort_by: sortBy }, index) => (
            <RadioButtonOuter
              key={sortBy}
              role="option"
              aria-selected={focusedIndex === index}
              tabIndex={-1}
              onClick={() => handleSortOptionClick(sortBy)}
              data-autotag={`sort-by-${sortBy}`}
            >
              <RadioButton
                value={selectedSortOption?.display_name}
                checked={selectedSortOption?.display_name === displayName}
                inputStyle={RadioInputStyle}
                inputCheckedStyle={RadioInputCheckedStyle}
                inputFocusStyle={RadioInputFocusStyle}
              />
              <DropdownListLabel
                $isSelected={selectedSortOption?.display_name === displayName}
              >
                <DynamicFormattedMessage
                  id={`grid_dynamic.${getSortIdRBKey(sortBy)}`}
                  defaultMessage={displayName}
                />
              </DropdownListLabel>
            </RadioButtonOuter>
          )
        )}
      </DropdownList>
    </SortDropdownContainer>
  );
};

export default SortDropdown;
